import type { AxiosResponse } from 'axios'

import productionSitesQueries from '@/api/modules/productionSitesQueries'
import reportReviewQueries from '@/api/modules/reportReviewQueries'
import reportSessionsQueries from '@/api/modules/reportSessionsQueries'
import reportTaskDiscoveryQueries from '@/api/modules/reportTaskDiscoveryQueries'
import type { ReportDetail, ReportDictionaryItem, ReportTaskListItem } from '@/api/schemas/reports/common'
import type {
    FindTaskPayloadSchema,
    GetReportingParametersPayloadSchema,
    GetScheduledTasksPayloadSchema,
    OperatorReportingInterruptedReport,
    OperatorReportingOrderJobListItem,
    OperatorReportingReportItemOrder,
    OperatorReportingReportItemOrdersFilters,
    OperatorReportingSession,
    OperatorReportingShippingGroup,
    OperatorReportingWorkspace,
    ReportingParametersResponseSchema,
    ReportingSessionResponseSchema,
    ReportItemOrdersResponseSchema,
    ReportOrderJobsResponseSchema,
    ReportOutOfSequenceCodesResponseSchema,
    ReportProcessesResponseSchema,
    ReportTaskSearchResponseSchema,
    ReportWorkspacesResponseSchema,
    ScheduledTasksResponseSchema,
    ShippingGroupsResponseSchema
} from '@/api/schemas/reports/operatorReporting'
import type { DetailedReportsResponseSchema, ReportShiftSummariesResponseSchema } from '@/api/schemas/reports/review'
import { REPORTS } from '@/constants'
import { t } from '@/i18n'
import { handleApiError } from '@/utils/apiHelpers'

class OperatorReportingService {
    async fetchOperatorWorkspaces(): Promise<AxiosResponse<ReportWorkspacesResponseSchema>> {
        try {
            return await productionSitesQueries.fetchProductionSitesBase({
                type_id: REPORTS.OPERATOR_REPORTING.WORKSPACE_TYPE_IDS
            })
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchWorkspacesError'))
            throw error
        }
    }

    async fetchWorkspace(workspaceId: number): Promise<AxiosResponse<OperatorReportingWorkspace>> {
        try {
            return await productionSitesQueries.fetchProductionSite(workspaceId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchWorkspaceError'))
            throw error
        }
    }

    async fetchUnfinishedReportsShiftSummary(userId: number): Promise<AxiosResponse<ReportShiftSummariesResponseSchema>> {
        try {
            return await reportSessionsQueries.getUnfinishedReportsShiftSummary(userId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchSessionError'))
            throw error
        }
    }

    async fetchCurrentUnfinishedReport(reportShiftSummaryId: number): Promise<AxiosResponse<DetailedReportsResponseSchema>> {
        try {
            return await reportReviewQueries.getUserDetailedReports(
                reportShiftSummaryId,
                `statusId=${REPORTS.REPORT_STATUSES.STARTED}`
            )
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchInterruptedReportsError'))
            throw error
        }
    }

    async fetchInterruptedReports(
        reportShiftSummaryId: number,
        machineId: number,
        queryString = ''
    ): Promise<AxiosResponse<DetailedReportsResponseSchema>> {
        try {
            const paginationQuery = queryString ? `&${queryString.replace(/^\?/, '')}` : ''

            return await reportReviewQueries.getUserDetailedReports(
                reportShiftSummaryId,
                `statusId=${REPORTS.REPORT_STATUSES.STOPPED}&machineId=${machineId}${paginationQuery}`
            )
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchInterruptedReportsError'))
            throw error
        }
    }

    async fetchReportStatuses(): Promise<AxiosResponse<{ results: ReportDictionaryItem[] }>> {
        try {
            return await reportReviewQueries.getReportStatuses()
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchReportStatusesError'))
            throw error
        }
    }

    async fetchScheduledTasks(payload: GetScheduledTasksPayloadSchema): Promise<AxiosResponse<ScheduledTasksResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getScheduledTasks(payload)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchScheduledTasksError'))
            throw error
        }
    }

    async fetchReportingParameters(
        payload: GetReportingParametersPayloadSchema
    ): Promise<AxiosResponse<ReportingParametersResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportingParameters(payload)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchReportingParametersError'))
            throw error
        }
    }

    async fetchReportProcesses(productionSiteId: number): Promise<AxiosResponse<ReportProcessesResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportProcesses(productionSiteId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchReportProcessesError'))
            throw error
        }
    }

    async fetchShippingGroups(): Promise<AxiosResponse<ShippingGroupsResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getShippingGroups()
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchShippingGroupsError'))
            throw error
        }
    }

    async fetchReportOutOfSequenceCodes(): Promise<AxiosResponse<ReportOutOfSequenceCodesResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportOutOfSequenceCodes()
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchOutOfSequenceCodesError'))
            throw error
        }
    }

    async fetchReportItemOrders(
        productionSiteId: number,
        filters: OperatorReportingReportItemOrdersFilters
    ): Promise<AxiosResponse<ReportItemOrdersResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportItemOrders(productionSiteId, filters)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchReportItemOrdersError'))
            throw error
        }
    }

    async findTasks(payload: FindTaskPayloadSchema): Promise<AxiosResponse<ReportTaskSearchResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.findTask(payload)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.findTasksError'))
            throw error
        }
    }

    async findTaskByNumber(payload: FindTaskPayloadSchema): Promise<AxiosResponse<ReportTaskSearchResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.findTaskByNumber(payload)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.findTaskByNumberError'))
            throw error
        }
    }

    async fetchReportItemOrderJobs(orderNumber: string): Promise<AxiosResponse<ReportOrderJobsResponseSchema>> {
        try {
            return await reportTaskDiscoveryQueries.getReportItemOrderJobs(`?orderNumber=${encodeURIComponent(orderNumber)}`)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchOrderJobsError'))
            throw error
        }
    }

    async startReportingSession(productionSiteId: number): Promise<AxiosResponse<ReportingSessionResponseSchema>> {
        try {
            return await reportSessionsQueries.startReportingSession(productionSiteId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.startSessionError'))
            throw error
        }
    }

    async stopReportingSession(
        reportShiftSummaryId: number,
        statusId: number
    ): Promise<AxiosResponse<ReportingSessionResponseSchema>> {
        try {
            return await reportSessionsQueries.stopReportingSession(reportShiftSummaryId, statusId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.stopSessionError'))
            throw error
        }
    }

    async fetchStoppedSessionDetails(reportShiftSummaryId: number): Promise<AxiosResponse<DetailedReportsResponseSchema>> {
        try {
            return await reportReviewQueries.getDetailedReports(reportShiftSummaryId)
        } catch (error) {
            handleApiError(error, t('reports.operatorReporting.service.fetchStoppedSessionDetailsError'))
            throw error
        }
    }

    isSystemReport(report: ReportDetail): boolean {
        return REPORTS.SYSTEM_PROCESS_CODE_IDS.some((processCodeId) => processCodeId === report.processCodeId)
    }

    normalizeInterruptedReport(report: ReportDetail | undefined): OperatorReportingInterruptedReport | null {
        if (!report) {
            return null
        }

        return {
            id: report.id,
            productionSiteId: report.productionSiteId,
            machineName: 'machineName' in report ? (report.machineName as string | null) : null,
            statusId: report.statusId,
            processCodeId: report.processCodeId
        }
    }

    mapSessionResponse(session: OperatorReportingSession | null): OperatorReportingSession | null {
        if (!session) {
            return null
        }

        return {
            ...session,
            startDateTime: session.startDateTime ?? session.startedAt ?? null,
            endDateTime: session.endDateTime ?? session.finishedAt ?? null
        }
    }

    getTaskNumber(task: ReportTaskListItem | null): string | null {
        if (!task) {
            return null
        }

        const rawTaskNumber = task.primaryNumber ?? task.procNumber ?? task.taskNumber ?? task.order ?? null

        return rawTaskNumber !== null && rawTaskNumber !== undefined ? String(rawTaskNumber) : null
    }

    mapOrderJobs(payload: ReportOrderJobsResponseSchema | null | undefined): OperatorReportingOrderJobListItem[] {
        return payload?.results || []
    }

    mapShippingGroups(payload: ShippingGroupsResponseSchema | null | undefined): OperatorReportingShippingGroup[] {
        return payload?.results || []
    }

    mapReportItemOrders(payload: ReportItemOrdersResponseSchema | null | undefined): OperatorReportingReportItemOrder[] {
        return payload?.results || []
    }
}

export const operatorReportingService = new OperatorReportingService()
